// mikro-beim-vorlesen.ts — merkt, ob die native App das Mikro beim Vorlesen offen hält.
//
// 13.10.2026 (Martin: „Mikro soll auch beim Vorlesen offen bleiben“): Die App meldet die Kippe `mikroBeimVorlesen`
// im Audio-Zustand (`getAudioSessionState` / `setPlaybackHiFi`, Build 106+). Hier steht nur der zuletzt gemeldete
// Wert, damit synchrone Stellen (tts-pcm.ts `sitzungHalten`) ihn lesen können. Fehlt das Feld (Build 105 und älter,
// Browser), gilt „aus" = altes Verhalten.

import { meldeSpur } from "./mikro-spur.js";

let stand = false;
let bekannt = false;
let abfrageLaeuft = false;

/** Den Wert aus einem Audio-Zustand der App übernehmen. Zustand ohne das Feld ⇒ aus. */
export function mikroBeimVorlesenMerken(state: { mikroBeimVorlesen?: unknown } | null | undefined): void {
  if (!state) return;
  stand = state.mikroBeimVorlesen === true;
  bekannt = true;
}

/** Hält die App das Mikro beim Vorlesen offen? (zuletzt gemeldeter Stand, sonst false) */
export function mikroBeimVorlesenAn(): boolean {
  return stand;
}

/** Einmal beim nativen Teil nachfragen, solange noch kein Zustand gemeldet wurde. Wirft nie. */
export function mikroBeimVorlesenAuffrischen(): void {
  if (bekannt || abfrageLaeuft) return;
  abfrageLaeuft = true;
  void import("./native-recorder.js")
    .then((m) => m.getAudioSessionState())
    .catch(() => null)
    .finally(() => {
      abfrageLaeuft = false;
    });
}

/**
 * Die gewählte Stellung der Einstellung („Mikro beim Vorlesen offen halten (Test)“, Vorgabe aus) an die native Kippe
 * geben — beim App-Start und nach jeder Änderung. Überstimmt den nativen Standard AN. Im Browser passiert nichts.
 * Wirft nie.
 *
 * ⚠️ 17.10.2026: Die Kippe liegt nativ auf dem Plugin „PawbotVoice“. Seit pre.3772 (14.10.) ging der Aufruf an
 *    „PawbotRecorder“, scheiterte dort („not implemented“) und wurde still geschluckt — der native Standard AN galt
 *    überall (`"mikroBeimVorlesen":true` in jeder Zustandsmeldung), die Stimme stockte auf dem iPhone. Seither geht er
 *    an „PawbotVoice“, und jedes Ergebnis steht im Journal: `[mikro-beim-vorlesen] gesetzt …` bzw. `setter-fehlt …`.
 *    Scheitert der Aufruf (alter Build), bleibt alles, wie es war (nativer Standard).
 */
export async function mikroBeimVorlesenAnwenden(an: boolean): Promise<void> {
  try {
    const { mikroBeimVorlesenSetzen } = await import("./native-voice.js");
    const r = await mikroBeimVorlesenSetzen(an);
    if ("fehler" in r) {
      if (r.fehler !== "kein-plugin") {
        meldeSpur("mikro-beim-vorlesen", `setter-fehlt plugin=PawbotVoice an=${an} fehler=${r.fehler}`);
      }
      return;
    }
    stand = r.an;
    bekannt = true;
    meldeSpur("mikro-beim-vorlesen", `gesetzt an=${an} app=${r.an}`);
    // Den vollen Stand von der App übernehmen (getAudioSessionState ruft mikroBeimVorlesenMerken).
    const m = await import("./native-recorder.js");
    await m.getAudioSessionState();
  } catch {
    /* nicht kritisch */
  }
}

/** Nur für Tests. */
export function _mikroBeimVorlesenVergessen(): void {
  stand = false;
  bekannt = false;
  abfrageLaeuft = false;
}
